import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, FolderOpen } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, LoadingBlock, EmptyState, formatDate } from "@/components/Common";
import StatusBadge, { STATUS_LABELS, useCaseLabel } from "@/components/StatusBadge";
import { walletName, problemLabel } from "@/data/site";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";

export default function CasesTable({ endpoint = "/cases", basePath = "/portal/cases", paged = true }) {
  const { lang } = useI18n();
  const L = (it, en, de) => (lang === "en" ? en : lang === "de" ? (de ?? it) : it);
  const caseLabel = useCaseLabel();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const pageSize = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = paged ? { page, page_size: pageSize, q: q || undefined, status: status || undefined } : { status: status || undefined };
      const { data } = await api.get(endpoint, { params });
      if (Array.isArray(data)) { setItems(data); setTotal(data.length); }
      else { setItems(data.items); setTotal(data.total); }
    } finally { setLoading(false); }
  }, [endpoint, page, q, status, paged]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder={L("Cerca riferimento…", "Search reference…", "Referenz suchen…")} className="pl-9 bg-navy-paper border-white/10" data-testid="cases-search" />
        </div>
        <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className="h-10 rounded-md bg-navy-paper border border-white/10 px-3 text-sm text-white" data-testid="cases-status-filter">
          <option value="">{L("Tutti gli stati", "All statuses", "Alle Status")}</option>
          {Object.keys(STATUS_LABELS).map((k) => <option key={k} value={k}>{caseLabel(k)}</option>)}
        </select>
      </div>

      {loading ? <LoadingBlock /> : items.length === 0 ? (
        <EmptyState icon={FolderOpen} title={L("Nessuna pratica trovata", "No cases found", "Keine Fälle gefunden")} description={L("Modifica i filtri o apri una nuova pratica.", "Adjust the filters or open a new case.", "Passen Sie die Filter an oder eröffnen Sie einen neuen Fall.")} />
      ) : (
        <>
          {/* Desktop table */}
          <Card className="hidden md:block overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-slate-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">{L("Riferimento", "Reference", "Referenz")}</th>
                  <th className="text-left px-4 py-3">{L("Wallet", "Wallet", "Wallet")}</th>
                  <th className="text-left px-4 py-3">{L("Problema", "Problem", "Problem")}</th>
                  <th className="text-left px-4 py-3">{L("Stato", "Status", "Status")}</th>
                  <th className="text-left px-4 py-3">{L("Aggiornata", "Updated", "Aktualisiert")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="border-t border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-4 py-3"><Link to={`${basePath}/${c.id}`} className="text-cyanx font-mono hover:text-white" data-testid={`case-row-${c.reference}`}>{c.reference}</Link></td>
                    <td className="px-4 py-3 text-slate-300">{walletName(c.wallet_type)}</td>
                    <td className="px-4 py-3 text-slate-400">{problemLabel(c.problem_type, lang)}</td>
                    <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                    <td className="px-4 py-3 text-slate-500">{formatDate(c.updated_at || c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {items.map((c) => (
              <Link key={c.id} to={`${basePath}/${c.id}`} className="block rounded-xl border border-white/10 bg-navy-paper p-4" data-testid={`case-card-${c.reference}`}>
                <div className="flex items-center justify-between">
                  <span className="text-cyanx font-mono text-sm">{c.reference}</span>
                  <StatusBadge status={c.status} />
                </div>
                <p className="mt-2 text-sm text-slate-300">{walletName(c.wallet_type)} · {problemLabel(c.problem_type, lang)}</p>
                <p className="text-xs text-slate-500 mt-1">{formatDate(c.updated_at || c.created_at)}</p>
              </Link>
            ))}
          </div>

          {paged && total > pageSize && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">{total} {L("risultati", "results", "Ergebnisse")}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="border-white/20" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>{L("Precedente", "Previous", "Zurück")}</Button>
                <Button size="sm" variant="outline" className="border-white/20" disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)}>{L("Successiva", "Next", "Weiter")}</Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
